import React, { useEffect, useMemo, useState } from "react";
import { FaBookOpen, FaDownload, FaFileAlt, FaSearch, FaSync } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { useStudentProfile } from "../context/StudentProfileContext";

const rowsFrom = (response) => {
  const value = response?.data?.data ?? response?.data ?? [];
  return Array.isArray(value) ? value : value.content || value.items || [];
};

export default function StudentLearningLibrary() {
  const { studentProfile } = useStudentProfile();
  const student = studentProfile?.studentInfo || {};
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("ALL");

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get(API_PATHS.student.learningResources());
      setResources(rowsFrom(response));
    } catch (error) {
      setResources([]);
      if (error?.response?.status !== 404) toast.error(error?.message || "Unable to load learning resources.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const shown = useMemo(() => resources.filter((item) => {
    const matchesType = type === "ALL" || String(item.type || item.resourceType).toUpperCase() === type;
    const haystack = [item.title, item.subject, item.description, item.className, item.grade].join(" ").toLowerCase();
    return matchesType && haystack.includes(query.trim().toLowerCase());
  }), [query, resources, type]);

  const openResource = async (item) => {
    try {
      const response = await api.get(API_PATHS.student.learningResourceAccess(item.id || item.resourceId));
      const url = response?.data?.data?.url || response?.data?.url || response?.data?.data?.signedUrl || response?.data?.signedUrl;
      if (!url) throw new Error("A secure file link was not returned.");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error?.message || "Unable to open this resource.");
    }
  };

  return <main className="min-h-full bg-slate-50 p-4 text-slate-900 sm:p-6">
    <div className="mx-auto max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-600">Learning library</p><h1 className="mt-2 text-3xl font-black">Notes &amp; Books</h1><p className="mt-2 text-sm text-slate-500">Resources approved for {student.studentClass || "your class"}{student.section ? ` · Section ${student.section}` : ""}.</p></div>
        <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold"><FaSync className={loading ? "animate-spin" : ""} /> Refresh</button>
      </div>
      <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row">
        <label className="relative flex-1"><FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" /><input value={query} onChange={(event) => setQuery(event.target.value)} className="w-full rounded-xl border border-slate-300 py-3 pl-11 pr-4 text-sm" placeholder="Search title or subject" /></label>
        <select value={type} onChange={(event) => setType(event.target.value)} className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold"><option value="ALL">All resources</option><option value="NOTE">Notes</option><option value="BOOK">Books</option></select>
      </div>
      {loading ? <div className="py-20 text-center text-slate-500">Loading your learning resources…</div> : shown.length === 0 ? <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center"><FaBookOpen className="mx-auto text-3xl text-slate-300" /><h2 className="mt-4 text-lg font-bold">No resources available yet</h2><p className="mt-2 text-sm text-slate-500">Your school will see resources here after ClassNX approval and publication.</p></div> : <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">{shown.map((item) => <article key={item.id || item.resourceId} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><div className="flex items-start gap-4"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-sky-50 text-sky-700">{String(item.type || item.resourceType).toUpperCase() === "BOOK" ? <FaBookOpen /> : <FaFileAlt />}</span><div className="min-w-0"><span className="text-[10px] font-black uppercase tracking-widest text-sky-600">{item.type || item.resourceType || "Resource"}</span><h2 className="mt-1 truncate font-bold">{item.title}</h2><p className="mt-1 text-xs text-slate-500">{item.subject || "General"} · {item.className || item.grade || student.studentClass || "Assigned class"}</p></div></div><p className="mt-4 line-clamp-2 text-sm leading-6 text-slate-600">{item.description || "School learning resource"}</p><button type="button" onClick={() => openResource(item)} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white"><FaDownload /> Open resource</button></article>)}</div>}
    </div>
  </main>;
}
